import { createContext, useContext, useEffect, useState } from 'react';
import { Session, User } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export type AppRole = 'admin' | 'prestataire' | 'user' | 'gouvernante';

type AuthContextType = {
  session: Session | null;
  user: User | null;
  userRole: AppRole | null;
  isLoading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, metadata: { first_name: string; last_name: string }) => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [userRole, setUserRole] = useState<AppRole | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Définir le gestionnaire d'événements d'authentification
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        
        // Remettre à zéro le rôle quand l'utilisateur se déconnecte
        if (event === 'SIGNED_OUT') {
          setUserRole(null);
        }
        
        // Récupérer le rôle de l'utilisateur après la connexion
        if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
          setTimeout(async () => {
            await fetchUserRole();
          }, 0);
        }
      }
    );

    // Vérifier la session actuelle
    const initializeAuth = async () => {
      try {
        setIsLoading(true);
        const { data: { session } } = await supabase.auth.getSession();
        
        if (session) {
          setSession(session);
          setUser(session.user);
          await fetchUserRole();
        }
      } catch (error) {
        console.error('Erreur d\'initialisation de l\'auth:', error);
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  // Récupérer le rôle de l'utilisateur depuis la base de données
  const fetchUserRole = async () => {
    try {
      const { data, error } = await supabase.rpc('get_current_user_role');
      
      if (error) {
        console.error('Erreur lors de la récupération du rôle:', error);
        return;
      }
      
      if (data) {
        setUserRole(data);
      }
    } catch (error) {
      console.error('Erreur lors de la récupération du rôle:', error);
    }
  };

  const linkExistingReservations = async (email: string) => {
    try {
      console.log('Recherche des réservations existantes pour:', email);
      
      // Rechercher les réservations avec cet email qui ne sont pas encore liées à un compte
      const { data: existingReservations, error: fetchError } = await supabase
        .from('reservations')
        .select('id, first_name, last_name, check_in, check_out, status')
        .eq('email', email);

      if (fetchError) {
        console.error('Erreur lors de la recherche des réservations:', fetchError);
        return;
      }

      if (existingReservations && existingReservations.length > 0) {
        console.log(`${existingReservations.length} réservation(s) trouvée(s) pour cet email`);
        
        // Afficher un message de confirmation
        const reservationText = existingReservations.length === 1 
          ? `1 réservation existante a été automatiquement liée à votre compte`
          : `${existingReservations.length} réservations existantes ont été automatiquement liées à votre compte`;
        
        toast.success(reservationText);
      } else {
        console.log('Aucune réservation existante trouvée pour cet email');
      }
    } catch (error) {
      console.error('Erreur lors de la liaison des réservations:', error);
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;
      toast.success('Connexion réussie');
    } catch (error: any) {
      toast.error(error.message || 'Erreur lors de la connexion');
      throw error;
    }
  };

  const signUp = async (email: string, password: string, metadata: { first_name: string; last_name: string }) => {
    try {
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: metadata,
        },
      });

      if (error) throw error;
      
      // Lier automatiquement les réservations existantes après la création du compte
      await linkExistingReservations(email);
      
      toast.success('Compte créé avec succès. Consultez votre email pour activer votre compte.');
    } catch (error: any) {
      toast.error(error.message || 'Erreur lors de la création du compte');
      throw error;
    }
  };

  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      toast.success('Déconnexion réussie');
    } catch (error: any) {
      toast.error(error.message || 'Erreur lors de la déconnexion');
    }
  };

  const resetPassword = async (email: string) => {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      
      if (error) throw error;
      toast.success('Instructions envoyées à votre email');
    } catch (error: any) {
      toast.error(error.message || 'Erreur lors de la réinitialisation du mot de passe');
      throw error;
    }
  };

  const value = {
    session,
    user,
    userRole,
    isLoading,
    signIn,
    signUp,
    signOut,
    resetPassword,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth doit être utilisé à l\'intérieur d\'un AuthProvider');
  }
  return context;
};
